import { useState } from 'react'
import { Link } from 'react-router-dom'
import * as Popover from '@radix-ui/react-popover'
import { Bell, BellOff, CheckCheck, Settings } from 'lucide-react'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { cn } from '@/lib/cn'
import { NotificationItem } from './NotificationItem'
import {
  useMarkAllRead,
  useMarkRead,
  useNotifications,
  usePreferences,
  useUnreadCount,
} from '@/hooks/useNotifications'

const PREVIEW_LIMIT = 10

/**
 * AF-134 — header bell + dropdown preview.
 *
 * - Bell shows the unread count badge (hidden when zero), and the
 *   aria-label includes the count for screen readers.
 * - Clicking opens a Radix Popover with the latest 10 notifications.
 * - Each row deep-links to the related entity; clicking marks it read.
 * - Footer: "Mark all read", "View all", and a quick link to preferences.
 * - When DND is on, render a small banner so the user knows quiet mode
 *   is active and clicks-through to preferences to clear it.
 */
export function NotificationBell() {
  const [open, setOpen] = useState(false)
  const unread = useUnreadCount()
  const list = useNotifications({ limit: PREVIEW_LIMIT })
  const prefs = usePreferences()
  const markRead = useMarkRead()
  const markAll = useMarkAllRead()

  const total = unread.data?.total ?? 0
  const critical = unread.data?.by_level.critical ?? 0
  const dnd = prefs.data?.dnd.enabled ?? false

  const items = list.data?.pages[0]?.notifications ?? []

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label={
            total === 0
              ? 'Notifications, no unread'
              : `Notifications, ${total} unread${critical > 0 ? `, ${critical} critical` : ''}`
          }
        >
          <span className="relative">
            {dnd ? <BellOff size={18} /> : <Bell size={18} />}
            {total > 0 && (
              <Badge
                variant={critical > 0 ? 'critical' : 'default'}
                className="absolute -right-2 -top-2 min-w-[1.25rem] justify-center px-1 py-0 text-[0.625rem] leading-tight"
              >
                {total > 99 ? '99+' : total}
              </Badge>
            )}
          </span>
        </Button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="end"
          sideOffset={6}
          className={cn(
            'z-50 w-[26rem] rounded-md border border-border bg-card text-card-fg outline-none',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
          )}
        >
          <header className="flex items-center justify-between border-b border-border px-3 py-2">
            <h2 className="text-sm font-semibold">Notifications</h2>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => markAll.mutate()}
              disabled={total === 0 || markAll.isPending}
              className="h-7 gap-1 px-2 text-xs"
            >
              <CheckCheck size={12} aria-hidden /> Mark all read
            </Button>
          </header>

          {dnd && (
            <p className="border-b border-border bg-status-pending/5 px-3 py-1.5 text-xs text-status-pending">
              Do-not-disturb is on. Critical alerts still come through.{' '}
              <Link to="/notifications/preferences" className="underline">
                Manage
              </Link>
            </p>
          )}

          <div className="max-h-96 overflow-y-auto p-1">
            {list.isLoading ? (
              <p className="px-3 py-6 text-center text-sm text-muted-fg">Loading…</p>
            ) : items.length === 0 ? (
              <p className="px-3 py-6 text-center text-sm text-muted-fg">
                You're all caught up.
              </p>
            ) : (
              <ul className="flex flex-col gap-0.5">
                {items.map((n) => (
                  <li key={n.id}>
                    <NotificationItem
                      notification={n}
                      dense
                      onSelect={() => {
                        if (n.read_at === null) markRead.mutate(n.id)
                        setOpen(false)
                      }}
                    />
                  </li>
                ))}
              </ul>
            )}
          </div>

          <footer className="flex items-center justify-between border-t border-border px-3 py-2">
            <Link
              to="/notifications/preferences"
              onClick={() => setOpen(false)}
              className="inline-flex items-center gap-1 text-xs text-muted-fg hover:text-fg"
            >
              <Settings size={12} aria-hidden /> Preferences
            </Link>
            <Link
              to="/notifications"
              onClick={() => setOpen(false)}
              className="text-xs font-medium text-primary hover:underline"
            >
              View all
            </Link>
          </footer>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  )
}
